import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputBase,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowLeft,
  Building2,
  User as UserIcon,
  CreditCard,
  Zap,
  Users,
  Key,
  Settings,
  Bell,
  LifeBuoy,
  LogOut,
  X,
  Search,
  SlidersHorizontal,
  Plus,
  ShieldAlert,
} from 'lucide-react';
import { restoreUserSession, userLogOut } from '../store/actions/auth';
import FullOverlayLoader from '../components/FullOverlayLoader';

// Styled Components for Search & Create in Header
const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: '4px 12px',
  backgroundColor: '#fff',
  height: 40,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 280,
  },
}));

const CreateButton = styled(Button)(() => ({
  borderRadius: 6,
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#1d1d1f',
  color: 'white',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#2c2c2e',
    boxShadow: 'none',
  },
}));

export default function MainLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // States to pass down via Outlet Context to child pages
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAppType, setFilterAppType] = useState('');
  const [page, setPage] = useState(1);

  // Filters Popover Anchor state
  const [anchorEl, setAnchorEl] = useState(null);

  // Restore user session on mount
  useEffect(() => {
    dispatch(restoreUserSession());
  }, [dispatch]);

  const handleOpenFilters = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (setter, val) => {
    setter(val);
    setPage(1);
  };

  if (isLoading || !isAuthenticated) {
    return <FullOverlayLoader />;
  }

  // Determine page title based on path
  let title = 'My Apps';
  if (path === '/dashboard/create-app') {
    title = 'Create App';
  } else if (path === '/dashboard/global-billing') {
    title = 'Accounts Billing';
  }

  const showBackButton = path !== '/dashboard/main';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfd', pb: 10 }}>
      {/* ─── Sticky Header Wrapper ─── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backdropFilter: 'blur(20px)',
          bgcolor: 'rgba(251, 251, 253, 0.8)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={{ xs: 2, md: 3 }}
            sx={{ width: '100%' }}
          >
            {/* Left side: Back Button OR Avatar + Title & Email */}
            <Stack direction="row" spacing={2} alignItems="center">
              {showBackButton ? (
                <IconButton
                  onClick={() => navigate('/dashboard/main')}
                  sx={{
                    border: '1px solid #d2d2d7',
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                    '&:hover': { bgcolor: '#f5f5f7' },
                  }}
                >
                  <ArrowLeft size={24} color="#1d1d1f" />
                </IconButton>
              ) : (
                <Avatar
                  onClick={() => setIsDrawerOpen(true)}
                  src={user?.profileImage}
                  alt={user?.firstName}
                  sx={{
                    width: 56,
                    height: 56,
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    '&:hover': {
                      border: '1px solid #3b5ee6',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Building2 size={28} color="#3b5ee6" />
                </Avatar>
              )}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Stack>

            {/* Right side: Search, Filter, and Create Buttons (Only on main dashboard page) */}
            {!showBackButton && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                {/* Search input field */}
                <SearchContainer>
                  <Search size={18} color="#999" sx={{ flexShrink: 0 }} />
                  <InputBase
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(1);
                    }}
                    sx={{ ml: 1, flex: 1, fontSize: '0.9rem' }}
                  />
                </SearchContainer>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <IconButton
                    onClick={handleOpenFilters}
                    sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: '7px', flexShrink: 0 }}
                  >
                    <SlidersHorizontal size={20} color="#666" />
                  </IconButton>

                  <CreateButton
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={() => navigate('/dashboard/create-app')}
                    sx={{ flexGrow: { xs: 1, sm: 0 } }}
                  >
                    Create app
                  </CreateButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>

      {/* ─── Page Content Container ─── */}
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Outlet
          context={{
            searchQuery,
            setSearchQuery,
            filterRole,
            setFilterRole,
            filterStatus,
            setFilterStatus,
            filterAppType,
            setFilterAppType,
            page,
            setPage,
          }}
        />
      </Container>

      {/* ─── Filters Popover ─── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { p: 3, width: 280, borderRadius: '12px', boxShadow: '0px 8px 30px rgba(0,0,0,0.08)' },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Filters
        </Typography>
        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel id="role-filter-label">My Role</InputLabel>
            <Select
              labelId="role-filter-label"
              value={filterRole}
              label="My Role"
              onChange={(e) => handleFilterChange(setFilterRole, e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filterStatus}
              label="Status"
              onChange={(e) => handleFilterChange(setFilterStatus, e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="type-filter-label">App Type</InputLabel>
            <Select
              labelId="type-filter-label"
              value={filterAppType}
              label="App Type"
              onChange={(e) => handleFilterChange(setFilterAppType, e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="bill-payment">Bill Payment</MenuItem>
              <MenuItem value="web-to-app">Web to App</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setFilterRole('');
              setFilterStatus('');
              setFilterAppType('');
              setPage(1);
              handleCloseFilters();
            }}
            fullWidth
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            Reset Filters
          </Button>
        </Stack>
      </Popover>

      {/* ─── Shared Global Profile Drawer ─── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            borderLeft: 'none',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
            bgcolor: '#fbfbfd',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ p: 3, pb: 4, bgcolor: '#ffffff', borderBottom: '1px solid #f0f0f0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Avatar
              src={user?.profileImage}
              alt={user?.firstName}
              sx={{
                width: 64,
                height: 64,
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              }}
            >
              <UserIcon size={32} color="#3b5ee6" />
            </Avatar>
            <IconButton
              onClick={() => setIsDrawerOpen(false)}
              sx={{
                bgcolor: '#f5f5f7',
                '&:hover': { bgcolor: '#e8e8ed' },
              }}
            >
              <X size={20} color="#1d1d1f" />
            </IconButton>
          </Stack>

          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                {user?.firstName || 'Demo'} {user?.lastName || 'User'}
              </Typography>
              <Chip
                label="PRO"
                size="small"
                sx={{
                  bgcolor: 'rgba(59, 94, 230, 0.1)',
                  color: '#3b5ee6',
                  fontWeight: 800,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: '#86868b' }}>
              {user?.email || 'demo.user@example.com'}
            </Typography>
          </Stack>
        </Box>

        {/* Drawer Menu Sections */}
        <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
          <Typography variant="overline" sx={{ px: 2, color: '#86868b', fontWeight: 700, letterSpacing: '0.05em' }}>
            Account
          </Typography>
          <List sx={{ mb: 2 }}>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <UserIcon size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Personal Profile"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setIsDrawerOpen(false);
                  navigate('/dashboard/global-billing');
                }}
                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CreditCard size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Billing & Invoices"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Zap size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Upgrade Plan"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          <Typography
            variant="overline"
            sx={{ px: 2, mt: 2, display: 'block', color: '#86868b', fontWeight: 700, letterSpacing: '0.05em' }}
          >
            Workspace
          </Typography>
          <List sx={{ mb: 2 }}>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Users size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Team Members"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Key size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="API Keys"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/dashboard/audit-logs')}
                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ShieldAlert size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Action Audit Trail"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          <Typography
            variant="overline"
            sx={{ px: 2, mt: 2, display: 'block', color: '#86868b', fontWeight: 700, letterSpacing: '0.05em' }}
          >
            Preferences
          </Typography>
          <List sx={{ mb: 2 }}>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Settings size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="System Settings"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Bell size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Notifications"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={{ borderRadius: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LifeBuoy size={20} color="#1d1d1f" />
                </ListItemIcon>
                <ListItemText
                  primary="Help & Support"
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500, color: '#1d1d1f' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>

        {/* Drawer Footer Actions */}
        <Box sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #f0f0f0' }}>
          <ListItemButton
            onClick={() => {
              dispatch(userLogOut());
              navigate('/login');
            }}
            sx={{
              borderRadius: 2,
              bgcolor: '#fff0f0',
              color: '#d32f2f',
              '&:hover': { bgcolor: '#ffe0e0' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogOut size={20} color="#d32f2f" />
            </ListItemIcon>
            <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} />
          </ListItemButton>
        </Box>
      </Drawer>
    </Box>
  );
}
